import { FC } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import { DashboardLocIdInfo } from "../../../../../../redux/actions/dashboard/dashboardTypes";

import { DataSBFilterValues } from "../../../../../../redux/actions/dataTable/DataTableTypes";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  QualityCriteriaData,
  AddingCriteria,
  AdvancedDeclarationOption,
} from "../../../../../../redux/slices/grainTracker/grainTrackerTypes";
import { stylesGW } from "../../../../grainWiseStylesMUIComponents";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type RenderQualityCriteriaInputsToCreateParams = {
  selectedCriteriaTemplate: QualityCriteriaData[];
  t: (arg0: string) => string;
  addingCriteria: AddingCriteria;
  setCriteriaToBin: (addingCriteria: AddingCriteria) => void;
  resetAddingCriteria: () => void;
  setAdvancedDeclarationOptionText: (
    advancedDeclarationOption: AdvancedDeclarationOption,
    searchText: string
  ) => void;
  advancedDeclarationOption: AdvancedDeclarationOption;
  setAvancedDeclarationOptionSelected: (values: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  }) => void;
  advancedDeclarationOptionSelected: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  };
  selectedCriteria: string | undefined;
};

const RenderQualityCriteriaInputsToCreate: FC<
  RenderQualityCriteriaInputsToCreateParams
> = ({
  selectedCriteriaTemplate,
  selectedCriteria,
  t,
  addingCriteria,
  setCriteriaToBin,
  resetAddingCriteria,
  setAdvancedDeclarationOptionText,
  advancedDeclarationOption,
  setAvancedDeclarationOptionSelected,
  advancedDeclarationOptionSelected,
}) => {
  if (
    selectedCriteriaTemplate[0]?.allotmentOperators?.every((x) => x !== "in")
  ) {
    return (
      <div className="container-criteria-values">
        <div className="should-be-GT-criteria">
          {t("Grain_tracker.should-be")}
        </div>
        <FormControl
          sx={stylesGW.RenderQualityCriteriaInputsToCreate.FormControlContainer}
        >
          <Select
            required
            onChange={(e) => {
              setCriteriaToBin({
                ...addingCriteria,
                values: {
                  ...addingCriteria.values,
                  allotmentOperator: e.target.value as string,
                  storageQualityCriterionId: selectedCriteriaTemplate[0].id,
                  allotmentValueArray: null,
                },
              });
            }}
          >
            {selectedCriteriaTemplate[0]?.allotmentOperators?.map((values) => {
              return (
                <MenuItem key={values} value={values}>
                  {values === "<"
                    ? t("Grain_tracker.strict-below")
                    : t("Grain_tracker.strict-above")}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <TextField
          label={""}
          sx={
            stylesGW.RenderQualityCriteriaInputsToCreate
              .FormControlContainerTextField
          }
          type="number"
          InputLabelProps={{ shrink: true }}
          value={
            parseFloat(addingCriteria.values.allotmentValue).toString() ?? 0
          }
          onChange={(e) => {
            const handleNumber = () => {
              if (
                selectedCriteriaTemplate[0]?.qualityCriterionUnit.symbolTextId
                  .text === "%"
              ) {
                return parseFloat(e.target.value) >= 100
                  ? "100"
                  : parseFloat(e.target.value).toFixed(2);
              } else {
                return parseFloat(e.target.value).toFixed(2);
              }
            };
            setCriteriaToBin({
              ...addingCriteria,
              values: {
                ...addingCriteria.values,
                allotmentValue: handleNumber().toString(),
              },
            });
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {
                  selectedCriteriaTemplate[0]?.qualityCriterionUnit.symbolTextId
                    .text
                }
              </InputAdornment>
            ),
            inputProps: { step: "any", min: 0 },
          }}
        />
        <IconButton
          onClick={() => {
            resetAddingCriteria();
          }}
          size="small"
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </div>
    );
  } else if (
    selectedCriteriaTemplate[0]?.allotmentOperators?.some((x) => x === "in")
  ) {
    const tagsOptions = advancedDeclarationOption?.tagsResults?.map(
      (values) => ({
        name: values.customerFacingTextId
          ? values.customerFacingTextId.text
          : values.defaultCustomerFacingText,
        id: values.id,
      })
    );
    const transform = tagsOptions?.map((c: any) => c);

    return (
      <>
        <Autocomplete
          multiple
          disableListWrap={true}
          options={tagsOptions as any}
          disableCloseOnSelect
          filterOptions={(options, params) => {
            const filter = createFilterOptions();
            const filtered = filter(options, params as any);
            return [
              { name: t("Dashboard.setAllLocations"), id: 0 },
              ...filtered,
            ] as any;
          }}
          value={advancedDeclarationOptionSelected.valuesSelected as any}
          getOptionLabel={(option: DashboardLocIdInfo) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_e, value, reason) => {
            if (reason === "clear" || reason === "removeOption")
              setAvancedDeclarationOptionSelected({
                allValuesSelected: false,
                valuesSelected: [],
              });

            if (
              reason === "selectOption" &&
              value.length === tagsOptions.length
            )
              setAvancedDeclarationOptionSelected({
                allValuesSelected: true,
                valuesSelected: transform as any,
              });

            setCriteriaToBin({
              ...addingCriteria,
              values: {
                ...addingCriteria.values,
                storageQualityCriterionId: selectedCriteriaTemplate[0].id,
                allotmentOperator: selectedCriteriaTemplate.filter(
                  (v) => v.id === selectedCriteria
                )[0].allotmentOperators[0],
              },
            });

            setAvancedDeclarationOptionSelected({
              allValuesSelected: false,
              valuesSelected: value,
            });

            if (reason === "selectOption" && tagsOptions.length === 0)
              console.log("");
          }}
          renderInput={(params: { InputLabelProps: any }) => {
            return (
              <TextField
                {...params}
                label={t("Common_options.type_to_search")}
                variant="outlined"
                size="medium"
                onChange={(e) => {
                  setAdvancedDeclarationOptionText(
                    advancedDeclarationOption,
                    e.target.value
                  );
                }}
                sx={stylesGW.RenderQualityCriteriaInputsToCreate.Autocomplete}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            );
          }}
          renderTags={(list) => {
            if (list.length === 1) {
              return (
                <Box
                  sx={
                    stylesGW.RenderQualityCriteriaInputsToCreate
                      .AutocompleteTags
                  }
                >
                  {list.map((value: any) => (
                    <Chip key={value.id} label={value.name} />
                  ))}
                </Box>
              );
            }

            if (list.length > 1) {
              return (
                <Box
                  sx={
                    stylesGW.RenderQualityCriteriaInputsToCreate
                      .AutocompleteTags
                  }
                >
                  <Chip
                    label={`${list[0].name} + ${list.length - 1} ${t(
                      "Common_options.others"
                    )}`}
                  />
                </Box>
              );
            }
          }}
          renderOption={(props, option, { selected }) => {
            if (option.id === 0) {
              return (
                <Box
                  key={option.id}
                  onMouseDown={(e) => e.preventDefault()}
                  pl={1.5}
                  py={0.5}
                  sx={{
                    position: "sticky",
                    top: "-1rem",
                    background: "#fff",
                    height: "60px",
                    zIndex: "2",
                    borderBottom: "1px solid",
                  }}
                >
                  <FormControlLabel
                    onClick={(e) => {
                      if (tagsOptions.length >= 1) {
                        e.preventDefault();
                        !advancedDeclarationOptionSelected.allValuesSelected
                          ? setAvancedDeclarationOptionSelected({
                              allValuesSelected: true,
                              valuesSelected: transform,
                            })
                          : setAvancedDeclarationOptionSelected({
                              allValuesSelected: false,
                              valuesSelected: [],
                            });
                      }
                    }}
                    disabled={tagsOptions.length === 0}
                    label={t("Dashboard.setAllLocations")}
                    control={
                      <Checkbox
                        disabled={tagsOptions.length === 0}
                        checked={
                          advancedDeclarationOptionSelected.allValuesSelected
                        }
                      />
                    }
                  />
                </Box>
              );
            }

            return (
              <li {...props}>
                <Checkbox
                  key={option.id}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={
                    option.id === 0
                      ? !!(
                          advancedDeclarationOptionSelected.valuesSelected.filter(
                            (x) => x.id !== undefined
                          ).length === tagsOptions.length
                        )
                      : selected
                  }
                />

                <div>{`${option.name}`}</div>
              </li>
            );
          }}
          renderGroup={(params) => params as unknown as React.ReactNode}
        />

        <IconButton
          onClick={() => {
            resetAddingCriteria();
          }}
          size="small"
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </>
    );
  }
  return (
    <div>
      <div></div>
    </div>
  );
};

export default RenderQualityCriteriaInputsToCreate;
